<script>
  import { onDestroy, onMount } from 'svelte';
  import { activeMatch, currentScreen, savedMatches } from '../store.js';
  import { getMeta, upsertSavedMatch, clearActiveMatch } from '../../store.js';
  import { toast, promptDialog } from '../../ui/dialogs.js';
  import { renderScorecardCanvas, renderMomCardCanvas, saveCanvasImage } from '../../share-images.js';

  // Player-of-the-Match share card — the "viral growth" tap.
  let isSharingMom = false;
  async function shareMomCard() {
    if (!mvp || !m) return;
    isSharingMom = true;
    try {
      const canvas = await renderMomCardCanvas(mvp, m);
      const result = await saveCanvasImage(canvas, `MOM_${mvp.name.replace(/\s+/g, '_')}.png`);
      if (result.ok) toast('🏆 MOM card ready — WhatsApp/Insta par share karo!', 'success');
      else toast('Card save failed: ' + (result.error?.message || ''), 'error');
    } catch (e) {
      toast('Card render failed: ' + (e.message || e), 'error');
    } finally {
      isSharingMom = false;
    }
  }
  import { recordGlobalPerformances } from '../../services/cloud-sync.js';
  import { buildFixtureResultFromMatch, postFixtureResult } from '../../services/tournament-cloud.js';
  import Icon from '../components/Icon.svelte';
  import WagonWheelModal from '../components/WagonWheelModal.svelte';
  import RecordingSavePanel from '../components/RecordingSavePanel.svelte';

  let showWagonWheel = false;

  async function downloadScorecard() {
    if (!m) return;
    try {
      const canvas = renderScorecardCanvas(m, name => getMeta(name).color);
      const result = await saveCanvasImage(canvas, `Scorecard_${m.teamA.name}_vs_${m.teamB.name}.png`);
      if (result.ok) toast('📄 Full scorecard image ready!', 'success');
      else toast('Scorecard save failed: ' + (result.error?.message || ''), 'error');
    } catch (e) {
      toast('Scorecard render failed: ' + (e.message || e), 'error');
    }
  }
  import { formatOvers, computeResult, isMaidenOver, createMatch, setOpeners, isInningsOver } from '../../scoring-logic.js';
  import { persist } from '../../store.js';
  import { applyDeviceSession } from '../../device-session.js';
  import { rankMatchPlayers } from '../../mvp-engine.js';

  $: matchMvpList = m ? rankMatchPlayers(m).slice(0, 3) : [];

  if (!$activeMatch) {
    currentScreen.set('home');
  }

  $: m = $activeMatch;
  // Safety net: compute the result if the screen was reached without one,
  // BUT only when the current innings is actually over. Without this guard,
  // navigating to Result after startSecondInnings (but before any chase balls)
  // would declare the 1st-innings team winner because 0 < target.
  onMount(() => {
    if ($activeMatch && !$activeMatch.result && isInningsOver($activeMatch)) {
      activeMatch.update(x => {
        x.result = computeResult(x);
        return x;
      });
    }
  });

  function upsertIntoSaved(matchValue) {
    const snap = JSON.parse(JSON.stringify(matchValue));
    delete snap._undoStack;
    const list = (Array.isArray($savedMatches) ? $savedMatches : []).filter(x => x?.id !== snap.id);
    list.push(snap);
    savedMatches.set(list);
    // Write only THIS match. persistSaved() cleared and rewrote the entire
    // matches store, so saving the 51st match rewrote all 50 previous ones
    // (~15 MB) to flash and froze the UI for hundreds of milliseconds.
    upsertSavedMatch(snap);
  }

  // Auto-save the completed scorecard the moment the result screen opens —
  // if the phone dies or the user later starts a new match without tapping
  // SAVE & FINISH, the match must not be lost. (Reactive: `m` is assigned
  // after the script body runs, so a plain init-time check would never fire.)
  let autoSaved = false;
  $: if (m && m.result && !autoSaved) {
    autoSaved = true;
    upsertIntoSaved(m);
    if (m.tournamentId && m.tournamentMatchId) {
      updateTournamentResult(m.tournamentId, m.tournamentMatchId, m);
    }
    // Ecosystem: publish per-player performances for every @username-linked
    // player, so their career stats update on THEIR phone too. Fire-and-forget;
    // deterministic doc ids make double-writes harmless.
    recordGlobalPerformances(m).then(n => {
      if (n > 0) console.log(`[Ecosystem] ${n} player performance(s) synced to cloud.`);
    }).catch(() => {});
    // Cloud tournament fixture? Push the result summary — every participant's
    // phone sees the points table update live. Idempotent (won't overwrite).
    if (m.cloudTournamentId && m.cloudFixtureId) {
      const summary = buildFixtureResultFromMatch(m);
      if (summary) {
        postFixtureResult(m.cloudTournamentId, m.cloudFixtureId, summary).then(ok => {
          if (ok) toast('🏆 Tournament points table updated!', 'success');
        }).catch(() => {});
      }
    }
  }

  onDestroy(() => {
    if (m && m.result) {
      clearActiveMatch();
    }
  });

  function updateTournamentResult(tournamentId, fixtureId, match) {
    try {
      const list = JSON.parse(localStorage.getItem('cs_tournaments_v1') || "[]");
      const tIdx = list.findIndex(t => t.id === tournamentId);
      if (tIdx === -1) return;

      const t = list[tIdx];
      const fIdx = t.fixtures.findIndex(f => f.id === fixtureId);
      if (fIdx === -1) return;

      const f = t.fixtures[fIdx];
      if (f.played) return;

      const res = match.result;
      f.played = true;
      f.matchId = match.id;
      f.winner = res.winner;
      f.margin = res.winner === "TIE" ? "" : `by ${res.margin} ${res.marginType}`;
      f.marginType = res.marginType;

      const teamAName = match.teamA.name;
      const teamBName = match.teamB.name;
      const teamAStats = t.pointsTable[teamAName];
      const teamBStats = t.pointsTable[teamBName];

      if (!teamAStats || !teamBStats) return;

      teamAStats.played += 1;
      teamBStats.played += 1;

      const maxBalls = match.overs * 6;
      const innA = match.innings.find(i => i.battingTeam === "A");
      const innB = match.innings.find(i => i.battingTeam === "B");

      if (!innA) return;

      const aAllOut = innA.wickets >= (match.lms ? match.teamA.players.length : match.teamA.players.length - 1);
      const bAllOut = innB ? innB.wickets >= (match.lms ? match.teamB.players.length : match.teamB.players.length - 1) : false;

      const aBallsFaced = aAllOut ? maxBalls : innA.balls;
      const aBallsBowled = innB ? (bAllOut ? maxBalls : innB.balls) : 0;
      const bBallsFaced = innB ? (bAllOut ? maxBalls : innB.balls) : 0;
      const bBallsBowled = aAllOut ? maxBalls : innA.balls;

      const aRunsScored = innA.totalRuns;
      const aRunsConceded = innB ? innB.totalRuns : 0;
      const bRunsScored = innB ? innB.totalRuns : 0;
      const bRunsConceded = innA.totalRuns;

      teamAStats.runsScored += aRunsScored;
      teamAStats.ballsFaced += aBallsFaced;
      teamAStats.runsConceded += aRunsConceded;
      teamAStats.ballsBowled += aBallsBowled;

      teamBStats.runsScored += bRunsScored;
      teamBStats.ballsFaced += bBallsFaced;
      teamBStats.runsConceded += bRunsConceded;
      teamBStats.ballsBowled += bBallsBowled;

      if (res.winner === "A") {
        teamAStats.won += 1;
        teamAStats.pts += 2;
        teamBStats.lost += 1;
      } else if (res.winner === "B") {
        teamBStats.won += 1;
        teamBStats.pts += 2;
        teamAStats.lost += 1;
      } else {
        teamAStats.tied += 1;
        teamAStats.pts += 1;
        teamBStats.tied += 1;
        teamBStats.pts += 1;
      }

      Object.keys(t.pointsTable).forEach(teamName => {
        const stats = t.pointsTable[teamName];
        const runsSc = stats.runsScored;
        const oversFa = stats.ballsFaced / 6;
        const runsCo = stats.runsConceded;
        const oversBo = stats.ballsBowled / 6;

        const scoredRate = oversFa > 0 ? runsSc / oversFa : 0;
        const concededRate = oversBo > 0 ? runsCo / oversBo : 0;
        stats.nrr = scoredRate - concededRate;
      });

      
      // Aggregate tournament player stats
      if (!t.playerStats) t.playerStats = {};
      // eslint-disable-next-line svelte/prefer-svelte-reactivity
      const processedPlayers = new Set();
      
      const addPlayerRecord = (pName, pTeam, run, ball, four, six, wkt, con, bld, mdn) => {
        if (!t.playerStats[pName]) {
          t.playerStats[pName] = {
            name: pName,
            team: pTeam,
            matches: 0,
            runs: 0,
            balls: 0,
            fours: 0,
            sixes: 0,
            highestScore: 0,
            wickets: 0,
            runsConceded: 0,
            ballsBowled: 0,
            maidens: 0
          };
        }
        const pObj = t.playerStats[pName];
        if (!processedPlayers.has(pName)) {
          pObj.matches += 1;
          processedPlayers.add(pName);
        }
        pObj.runs += run;
        pObj.balls += ball;
        pObj.fours += four;
        pObj.sixes += six;
        if (run > pObj.highestScore) pObj.highestScore = run;
        pObj.wickets += wkt;
        pObj.runsConceded += con;
        pObj.ballsBowled += bld;
        pObj.maidens += mdn;
      };

      // Add Team A batsmen (include duck-outs: 0 runs off 0 balls but dismissed
      // — matches the cloud-stats condition so a diamond duck still counts).
      innA.batsmen.forEach(b => {
        if (b.balls > 0 || b.runs > 0 || b.out) {
          addPlayerRecord(b.name, teamAName, b.runs, b.balls, b.fours, b.sixes, 0, 0, 0, 0);
        }
      });
      // Add Team B batsmen (if innings exists)
      if (innB) {
        innB.batsmen.forEach(b => {
          if (b.balls > 0 || b.runs > 0 || b.out) {
            addPlayerRecord(b.name, teamBName, b.runs, b.balls, b.fours, b.sixes, 0, 0, 0, 0);
          }
        });
      }

      // Add Team A bowlers (bowling to Team B, so they belong to Team A)
      if (innB) {
        innB.bowlers.forEach(b => {
          if (b.balls > 0) {
            addPlayerRecord(b.name, teamAName, 0, 0, 0, 0, b.wickets, b.runs, b.balls, bowlerMaidens(innB, b.name));
          }
        });
      }
      // Add Team B bowlers (bowling to Team A, so they belong to Team B)
      innA.bowlers.forEach(b => {
        if (b.balls > 0) {
          addPlayerRecord(b.name, teamBName, 0, 0, 0, 0, b.wickets, b.runs, b.balls, bowlerMaidens(innA, b.name));
        }
      });

      list[tIdx] = t;
      localStorage.setItem('cs_tournaments_v1', JSON.stringify(list));
    } catch (err) {
      console.warn("Tournament result update failed:", err);
    }
  }

  let title = "MATCH RESULT";
  let name = "";
  let margin = "";

  $: {
    if (m && m.result) {
      if (m.result.winner === "TIE") { name = "Match Tied"; }
      else if (m.result.winner === "COMPLETED") { name = "Innings Completed"; title = "MATCH COMPLETED"; }
      else { 
        name = (m.result.winner === "A" ? m.teamA.name : m.teamB.name) + " WIN"; 
        margin = `by ${m.result.margin} ${m.result.marginType}`; 
      }
    }
  }

  function getTeamName(inn) {
    return inn.battingTeam === "A" ? m.teamA.name : m.teamB.name;
  }

  function econ(runs, balls) {
    if (balls === 0) return "0.00";
    return ((runs / balls) * 6).toFixed(2);
  }

  function bowlerMaidens(inn, bowlerName) {
    const overs = new Set(inn.ballLog.filter(b => b.bowler === bowlerName).map(b => b.over));
    let count = 0;
    overs.forEach(ov => { if (isMaidenOver(inn, ov)) count++; });
    return count;
  }

  async function saveAndFinish() {
    upsertIntoSaved(m); // upsert by id — re-saving must not create duplicates
    await clearActiveMatch(m.id);
    currentScreen.set('home');
  }

  // ── Super Over tied? One tap starts a fresh decider with the same players,
  // batting order flipped (real-cricket style).
  $: superOverTied = !!(m && m.result?.winner === "TIE" && m.mode === "challenge" && m.challenge?.type === "super_over");

  async function playAnotherSuperOver() {
    upsertIntoSaved(m); // keep the tied match in history first
    const prevBatFirst = m.innings[0]?.battingTeam === "A" ? "A" : "B";
    const nextBatFirst = prevBatFirst === "A" ? "B" : "A";
    const teamA = JSON.parse(JSON.stringify(m.teamA));
    const teamB = JSON.parse(JSON.stringify(m.teamB));
    const deviceRole = m._deviceRole || "single_score";
    await clearActiveMatch(m.id);

    const decider = createMatch({
      teamA, teamB,
      overs: m.overs,
      mode: "challenge",
      lms: true,
      toss: { winner: nextBatFirst, choice: "bat" },
      challenge: { type: "super_over", targetValue: 0 }
    });
    const batters = nextBatFirst === "A" ? teamA.players : teamB.players;
    const bowlers = nextBatFirst === "A" ? teamB.players : teamA.players;
    setOpeners(decider, batters[0], batters.length > 1 ? batters[1] : null, bowlers[0]);
    applyDeviceSession(decider, deviceRole);
    activeMatch.set(decider);
    persist();
    toast("⚡ Decider super over — " + (nextBatFirst === "A" ? teamA.name : teamB.name) + " bat first!", "success");
    currentScreen.set(deviceRole === "single_record" ? "recording" : "scoring");
  }

  function buildShareText() {
    const lines = [];
    lines.push(`🏏 ${m.teamA.name} vs ${m.teamB.name} (${m.overs} overs)`);
    lines.push(name + (margin ? ` ${margin}` : ''));
    lines.push('');
    for (const inn of m.innings) {
      lines.push(`${getTeamName(inn)}: ${inn.totalRuns}/${inn.wickets} (${formatOvers(inn.balls)})`);
      const topBats = [...inn.batsmen].filter(b => b.balls > 0 || b.runs > 0)
        .sort((a, b) => b.runs - a.runs).slice(0, 2);
      for (const b of topBats) lines.push(`  ${b.name} ${b.runs}${b.out ? '' : '*'} (${b.balls})`);
      const topBowl = [...inn.bowlers].filter(b => b.balls > 0)
        .sort((a, b) => (b.wickets - a.wickets) || (a.runs - b.runs))[0];
      if (topBowl) lines.push(`  ⚡ ${topBowl.name} ${topBowl.wickets}-${topBowl.runs} (${formatOvers(topBowl.balls)})`);
      lines.push('');
    }
    lines.push('— scored with FFPL Cricket Scorer');
    return lines.join('\n');
  }

  async function shareResult() {
    const text = buildShareText();
    // navigator.share must be invoked synchronously from the tap gesture
    if (navigator.share) {
      try { await navigator.share({ text }); } catch(e) { if (e.name !== 'AbortError') console.warn('Share failed:', e); }
    } else {
      navigator.clipboard?.writeText(text)
        .then(() => toast('Scorecard copied — paste it in WhatsApp!', 'success'))
        .catch(() => promptDialog('Copy the scorecard text below:', { title: 'Share scorecard', value: text }));
    }
  }

  // ═══ MVP / MAN OF THE MATCH CALCULATION ═══
  $: mvp = (() => {
    if (!m || !m.innings) return null;
    const playersMap = {};

    m.innings.forEach(inn => {
      const battingTeamName = inn.battingTeam === "A" ? m.teamA.name : m.teamB.name;
      const bowlingTeamName = inn.battingTeam === "A" ? m.teamB.name : m.teamA.name;

      inn.batsmen.forEach(b => {
        if (!playersMap[b.name]) {
          playersMap[b.name] = { name: b.name, team: battingTeamName, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, bowledRuns: 0, bowledBalls: 0, maidens: 0 };
        }
        playersMap[b.name].runs += b.runs;
        playersMap[b.name].balls += b.balls;
        playersMap[b.name].fours += b.fours;
        playersMap[b.name].sixes += b.sixes;
      });

      inn.bowlers.forEach(bwl => {
        if (!playersMap[bwl.name]) {
          playersMap[bwl.name] = { name: bwl.name, team: bowlingTeamName, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, bowledRuns: 0, bowledBalls: 0, maidens: 0 };
        }
        playersMap[bwl.name].wickets += bwl.wickets;
        playersMap[bwl.name].bowledRuns += bwl.runs;
        playersMap[bwl.name].bowledBalls += bwl.balls;
        playersMap[bwl.name].maidens += bowlerMaidens(inn, bwl.name);
      });
    });

    let bestPlayer = null;
    let maxPoints = -1;

    // Winning side gets a bonus — a match-winning knock should outrank a
    // similar (or slightly bigger) effort from the losing side.
    const winnerLetter = m.result?.winner;
    const winnerTeamName = winnerLetter === "A" ? m.teamA.name : winnerLetter === "B" ? m.teamB.name : null;
    // Short formats (super over etc.) never reach 5 balls — scale the
    // strike-rate qualification down with match length.
    const srMinBalls = Math.max(2, Math.min(5, Math.round((m.overs || 1) * 3)));

    Object.values(playersMap).forEach(p => {
      let pts = p.runs;
      pts += p.fours * 1;
      pts += p.sixes * 2;
      if (p.balls >= srMinBalls) {
        const sr = (p.runs / p.balls) * 100;
        if (sr >= 200) pts += 10;
        else if (sr >= 150) pts += 5;
      }

      pts += p.wickets * 20;
      pts += p.maidens * 10;
      if (p.bowledBalls >= 6) {
        const econVal = (p.bowledRuns / p.bowledBalls) * 6;
        if (econVal < 6.0) pts += 15;
        else if (econVal < 8.0) pts += 8;
      }

      if (winnerTeamName && p.team === winnerTeamName) {
        pts += 12;
      }

      p.points = pts;

      if (pts > maxPoints) {
        maxPoints = pts;
        bestPlayer = p;
      }
    });

    return bestPlayer;
  })();

  // Helper function to draw rounded rectangles on canvas
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  // Draw over-by-over cumulative progress line graph
  import { tick } from 'svelte';
  async function drawRunRateChart() {
    await tick();
    const canvas = document.getElementById('run-rate-chart');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const w = rect.width;
    const h = rect.height;

    // Draw background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, w, h);

    if (!m || !m.innings) return;

    const inningsData = m.innings.map(inn => {
      const data = [0];
      let runningScore = 0;
      for (let o = 0; o < m.overs; o++) {
        const balls = inn.ballLog.filter(b => b.over === o);
        if (balls.length > 0) {
          runningScore += balls.reduce((s, b) => s + b.runs, 0);
          data.push(runningScore);
        } else {
          const finishedOvers = Math.floor(inn.balls / 6);
          if (o < finishedOvers) {
            data.push(runningScore);
          } else if (o === finishedOvers && inn.balls % 6 > 0) {
            runningScore += inn.ballLog.filter(b => b.over === o).reduce((s, b) => s + b.runs, 0);
            data.push(runningScore);
          }
          break;
        }
      }
      const tName = inn.battingTeam === "A" ? m.teamA.name : m.teamB.name;
      return {
        team: tName,
        color: getMeta(tName).color || '#3b82f6',
        runs: data
      };
    });

    const maxScore = Math.max(10, ...inningsData.flatMap(d => d.runs));
    const maxOvers = m.overs;

    const padL = 40, padR = 20, padT = 20, padB = 30;
    const graphW = w - padL - padR;
    const graphH = h - padT - padB;

    // Y Axis Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const val = Math.round((maxScore / 4) * i);
      const y = padT + graphH - (val / maxScore) * graphH;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(w - padR, y);
      ctx.stroke();

      ctx.fillStyle = '#9ca3af';
      ctx.font = '10px ui-monospace, monospace';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(val, padL - 8, y);
    }

    // X Axis Overs
    for (let o = 0; o <= maxOvers; o += Math.max(1, Math.round(maxOvers / 5))) {
      const x = padL + (o / maxOvers) * graphW;
      ctx.fillStyle = '#9ca3af';
      ctx.font = '10px ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(o, x, padT + graphH + 8);
    }

    // Plot Innings Lines
    inningsData.forEach((d, idx) => {
      ctx.strokeStyle = d.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      d.runs.forEach((score, overIdx) => {
        const x = padL + (overIdx / maxOvers) * graphW;
        const y = padT + graphH - (score / maxScore) * graphH;
        if (overIdx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // Wickets markers
      ctx.fillStyle = '#ef4444';
      d.runs.forEach((score, overIdx) => {
        const wktsThisOver = m.innings[idx].ballLog.filter(b => b.over === overIdx - 1 && b.type === 'wicket');
        if (wktsThisOver.length > 0) {
          const x = padL + (overIdx / maxOvers) * graphW;
          const y = padT + graphH - (score / maxScore) * graphH;
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });
    });
  }

  $: if (m && m.result) {
    setTimeout(drawRunRateChart, 100);
  }

  // Draw 1080x1080 Match Poster Canvas and download
  function downloadPoster() {
    if (!m || !m.result) return;
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');

    // Gradient Backdrop
    const grad = ctx.createLinearGradient(0, 0, 0, 1080);
    grad.addColorStop(0, '#0f172a');
    grad.addColorStop(1, '#020617');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1080, 1080);

    // Diagonal dynamic slash overlay
    ctx.fillStyle = 'rgba(255, 255, 255, 0.015)';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(1080, 0);
    ctx.lineTo(0, 1080);
    ctx.closePath();
    ctx.fill();

    // Title Header
    ctx.fillStyle = '#94a3b8';
    ctx.font = '900 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(m.tournamentId ? "TOURNAMENT MATCH REPORT" : "MATCH REPORT", 540, 60);

    const teamAName = m.teamA.name;
    const teamBName = m.teamB.name;
    const colorA = getMeta(teamAName).color || '#ff4500';
    const colorB = getMeta(teamBName).color || '#00d4ff';

    // Team A Name
    ctx.fillStyle = colorA;
    ctx.font = '900 48px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(teamAName.toUpperCase(), 300, 200);

    // Team B Name
    ctx.fillStyle = colorB;
    ctx.font = '900 48px sans-serif';
    ctx.fillText(teamBName.toUpperCase(), 780, 200);

    // VS text
    ctx.fillStyle = '#64748b';
    ctx.font = 'italic 900 36px sans-serif';
    ctx.fillText('VS', 540, 210);

    // Scores
    const innA = m.innings.find(i => i.battingTeam === "A");
    const scoreA = innA ? innA.totalRuns + '/' + innA.wickets : '0/0';
    const oversA = innA ? '(' + formatOvers(innA.balls) + ')' : '(0.0)';
    ctx.fillStyle = '#fff';
    ctx.font = '900 80px sans-serif';
    ctx.fillText(scoreA, 300, 280);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '800 32px sans-serif';
    ctx.fillText(oversA, 300, 370);

    const innB = m.innings.find(i => i.battingTeam === "B");
    const scoreB = innB ? innB.totalRuns + '/' + innB.wickets : '0/0';
    const oversB = innB ? '(' + formatOvers(innB.balls) + ')' : '(0.0)';
    ctx.fillStyle = '#fff';
    ctx.font = '900 80px sans-serif';
    ctx.fillText(scoreB, 780, 280);
    ctx.fillStyle = '#94a3b8';
    ctx.font = '800 32px sans-serif';
    ctx.fillText(oversB, 780, 370);

    // Result Text Banner
    ctx.fillStyle = '#fbbf24';
    ctx.font = '900 38px sans-serif';
    ctx.fillText(name + (margin ? ' ' + margin : ''), 540, 460);

    // Divider Line
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(100, 520);
    ctx.lineTo(980, 520);
    ctx.stroke();

    // Man of the Match Card
    if (mvp) {
      ctx.fillStyle = 'rgba(30, 27, 75, 0.4)';
      ctx.strokeStyle = 'rgba(192, 132, 252, 0.2)';
      ctx.lineWidth = 2;
      roundRect(ctx, 240, 560, 600, 180, 16);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#c084fc';
      ctx.font = '900 20px sans-serif';
      ctx.fillText('🏆  MAN OF THE MATCH  🏆', 540, 585);

      ctx.fillStyle = '#fff';
      ctx.font = '900 42px sans-serif';
      ctx.fillText(mvp.name, 540, 640);

      ctx.fillStyle = '#94a3b8';
      ctx.font = '700 22px sans-serif';
      let statsStr = mvp.team;
      if (mvp.runs > 0) statsStr += '  |  ' + mvp.runs + ' Runs (' + mvp.balls + 'b)';
      if (mvp.wickets > 0) statsStr += '  |  ' + mvp.wickets + ' Wickets (' + formatOvers(mvp.bowledBalls) + ' ov)';
      ctx.fillText(statsStr, 540, 700);
    }

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath();
    ctx.moveTo(100, 780);
    ctx.lineTo(980, 780);
    ctx.stroke();

    // Top Performers columns
    ctx.textAlign = 'left';
    ctx.fillStyle = colorA;
    ctx.font = '900 24px sans-serif';
    ctx.fillText(teamAName.toUpperCase() + ' PERFORMERS', 150, 820);
    
    let yPos = 870;
    if (innA) {
      const topBatsA = [...innA.batsmen].filter(b => b.balls > 0).sort((a,b) => b.runs - a.runs).slice(0, 2);
      topBatsA.forEach(b => {
        ctx.fillStyle = '#fff';
        ctx.font = '700 20px sans-serif';
        ctx.fillText(b.name + '  ' + b.runs + (b.out ? '' : '*') + ' (' + b.balls + 'b)', 150, yPos);
        yPos += 35;
      });
      const topBowlA = [...innA.bowlers].filter(b => b.balls > 0).sort((a,b) => b.wickets - a.wickets || a.runs - b.runs)[0];
      if (topBowlA) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '700 20px sans-serif';
        ctx.fillText('⚡ ' + topBowlA.name + '  ' + topBowlA.wickets + '-' + topBowlA.runs + ' (' + formatOvers(topBowlA.balls) + ' ov)', 150, yPos);
      }
    }

    ctx.fillStyle = colorB;
    ctx.font = '900 24px sans-serif';
    ctx.fillText(teamBName.toUpperCase() + ' PERFORMERS', 590, 820);

    yPos = 870;
    if (innB) {
      const topBatsB = [...innB.batsmen].filter(b => b.balls > 0).sort((a,b) => b.runs - a.runs).slice(0, 2);
      topBatsB.forEach(b => {
        ctx.fillStyle = '#fff';
        ctx.font = '700 20px sans-serif';
        ctx.fillText(b.name + '  ' + b.runs + (b.out ? '' : '*') + ' (' + b.balls + 'b)', 590, yPos);
        yPos += 35;
      });
      const topBowlB = [...innB.bowlers].filter(b => b.balls > 0).sort((a,b) => b.wickets - a.wickets || a.runs - b.runs)[0];
      if (topBowlB) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '700 20px sans-serif';
        ctx.fillText('⚡ ' + topBowlB.name + '  ' + topBowlB.wickets + '-' + topBowlB.runs + ' (' + formatOvers(topBowlB.balls) + ' ov)', 590, yPos);
      }
    }

    ctx.fillStyle = '#475569';
    ctx.font = '800 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SCORED WITH FFPL CRICKET SCORER', 540, 1030);

    // Save — native-safe (share sheet in the APK, download on web)
    saveCanvasImage(canvas, 'Match_Poster_' + teamAName + '_vs_' + teamBName + '.png').then(result => {
      if (result.ok) toast('🖼️ Poster ready!', 'success');
      else toast('Poster save failed: ' + (result.error?.message || ''), 'error');
    });
  }
</script>

{#if showWagonWheel}
  <WagonWheelModal on:select={() => showWagonWheel = false} match={$activeMatch} />
{/if}

{#if m}
<div class="screen">
  <div class="winner-banner">
    <div class="w-title">{title}</div>
    <div class="w-name">{name || "—"}</div>
    {#if margin}
      <div class="w-margin">{margin}</div>
    {/if}
  </div>

  <RecordingSavePanel />

  {#if superOverTied}
    <button class="btn btn-primary btn-block" style="margin:10px 0 4px; padding:14px; font-weight:900; font-size:15px; background:linear-gradient(135deg,#f59e0b,#dc2626); border:none;" on:click={playAnotherSuperOver}>
      ⚡ TIED! PLAY ANOTHER SUPER OVER
    </button>
  {/if}

  {#if mvp}
    <div class="card" style="margin-top:-6px; margin-bottom:12px; background: linear-gradient(135deg, #1e1b4b 0%, #311042 100%); border: 1px solid #c084fc; text-align:center; padding: 12px;">
      <div style="font-size: 11px; font-weight: 800; color:#c084fc; letter-spacing:1px; text-transform:uppercase;">🏆 MAN OF THE MATCH</div>
      <div style="font-size: 18px; font-weight: 900; color:#fff; margin-top:4px;">{mvp.name}</div>
      <div class="meta" style="font-size:11px; margin-top:2px; color:#a1a1aa;">
        {mvp.team} ·
        {#if mvp.runs > 0}{mvp.runs} runs ({mvp.balls}b){/if}
        {#if mvp.runs > 0 && mvp.wickets > 0} & {/if}
        {#if mvp.wickets > 0}{mvp.wickets} wkts ({formatOvers(mvp.bowledBalls)} ov){/if}
      </div>
      <button class="btn btn-primary" style="margin-top:10px; padding:8px 18px; font-size:12px; font-weight:900; background:linear-gradient(135deg,#7c3aed,#c084fc); border:none; display:inline-flex; align-items:center; gap:6px;" disabled={isSharingMom} on:click={shareMomCard}>
        <Icon name="share" size={13} /> {isSharingMom ? 'MAKING CARD...' : 'SHARE MOM CARD'}
      </button>
    </div>
  {/if}

  {#if matchMvpList && matchMvpList.length > 0}
    <div class="card" style="margin-bottom:12px; background: rgba(15, 23, 42, 0.9); border: 1px solid rgba(251, 191, 36, 0.3); padding: 12px;">
      <div style="font-size: 11px; font-weight: 800; color:#fbbf24; letter-spacing:1px; text-transform:uppercase; margin-bottom:8px;">🔥 TOP MATCH PERFORMERS (MVP LEADERBOARD)</div>
      <div style="display:flex; flex-direction:column; gap:6px;">
        {#each matchMvpList as p, idx (idx)}
          <div style="display:flex; justify-content:space-between; align-items:center; background:rgba(30,41,59,0.5); padding:6px 10px; border-radius:6px; font-size:12px;">
            <div style="display:flex; align-items:center; gap:8px;">
              <span style="font-weight:900; color:{idx === 0 ? '#fbbf24' : idx === 1 ? '#94a3b8' : '#b45309'};">#{idx + 1}</span>
              <span style="font-weight:800; color:#fff;">{p.name}</span>
            </div>
            <div style="display:flex; align-items:center; gap:8px;">
              <span style="font-size:10px; padding:2px 6px; border-radius:10px; background:{p.tier.color}20; color:{p.tier.color}; font-weight:800;">{p.tier.badge}</span>
              <span style="font-weight:900; color:#fbbf24;">{p.mvpPoints} PTS</span>
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}

  {#each m.innings as inn, idx (idx)}
    <div class="scorecard card">
      <div style="display:flex; justify-content:space-between; align-items:center; font-weight:800;margin-bottom:6px">
        <div>{idx === 0 ? "1st Innings" : "2nd Innings"} — {getTeamName(inn)}: {inn.totalRuns}/{inn.wickets} ({formatOvers(inn.balls)})</div>
        <button class="btn btn-ghost" style="font-size:11px; padding:4px 8px; border:1px solid rgba(255,255,255,0.2); border-radius:4px; color:#fff;" on:click={() => showWagonWheel = true}>WAGON WHEEL</button>
      </div>

      <!-- BATTING TABLE -->
      <table>
        <caption>BATTING</caption>
        <thead>
          <tr>
            <th>Batsman</th>
            <th class="r">R</th>
            <th class="r">B</th>
            <th class="r">4s</th>
            <th class="r">6s</th>
            <th>Out</th>
          </tr>
        </thead>
        <tbody>
          {#each inn.batsmen.filter(b => b.balls > 0 || b.out || [inn.currentStriker, inn.currentNonStriker].includes(b.name)) as b, i_each (b.id || b.name || i_each)}
            <tr>
              <td>{b.name}{b.name === inn.currentStriker ? " *" : ""}</td>
              <td class="r">{b.runs}</td>
              <td class="r">{b.balls}</td>
              <td class="r">{b.fours}</td>
              <td class="r">{b.sixes}</td>
              <td>{b.out ? (b.outType || "out") : "not out"}</td>
            </tr>
          {/each}
        </tbody>
      </table>

      <!-- BOWLING TABLE -->
      <table>
        <caption>BOWLING</caption>
        <thead>
          <tr>
            <th>Bowler</th>
            <th class="r">O</th>
            <th class="r">M</th>
            <th class="r">R</th>
            <th class="r">W</th>
            <th class="r">Econ</th>
          </tr>
        </thead>
        <tbody>
          {#each inn.bowlers.filter(b => b.balls > 0) as b, i_each (b.id || b.name || i_each)}
            <tr>
              <td>{b.name}</td>
              <td class="r">{formatOvers(b.balls)}</td>
              <td class="r">{bowlerMaidens(inn, b.name)}</td>
              <td class="r">{b.runs}</td>
              <td class="r">{b.wickets}</td>
              <td class="r">{econ(b.runs, b.balls)}</td>
            </tr>
          {/each}
        </tbody>
      </table>

      <div class="muted" style="font-size:11px;margin-top:6px">
        Extras: Wd {inn.extras.wide} • Nb {inn.extras.nb} • B {inn.extras.b} • Lb {inn.extras.lb}
      </div>
    </div>
  {/each}

  <!-- RUN RATE PROGRESS CHART -->
  <div class="card" style="margin-top:12px; margin-bottom: 12px;">
    <h3 style="margin:0 0 10px 0; color:#fff; text-align:left; font-size:13px; letter-spacing:0.5px;">📈 CUMULATIVE RUNS PROGRESS</h3>
    <canvas id="run-rate-chart" style="width: 100%; height: 180px; background: #0f172a; border-radius: 8px; border: 1px solid #1e293b;"></canvas>
  </div>

  <div style="display:flex; gap:8px; margin-bottom:8px">
    <button class="btn btn-ghost" style="flex:1; display:flex; align-items:center; justify-content:center; gap:6px;" on:click={shareResult}>
      <Icon name="share" size={14} /> SHARE
    </button>
    <button class="btn btn-ghost" style="flex:1; border: 1px solid #fbbf24; color: #fde047; display:flex; align-items:center; justify-content:center; gap:6px;" on:click={downloadPoster}>
      <Icon name="image" size={14} /> POSTER
    </button>
    <button class="btn btn-ghost" style="flex:1; border: 1px solid #34d399; color: #6ee7b7; display:flex; align-items:center; justify-content:center; gap:6px;" on:click={downloadScorecard}>
      <Icon name="file-text" size={14} /> SCORECARD
    </button>
  </div>
  <button class="btn btn-primary btn-block" on:click={saveAndFinish}>
    SAVE & FINISH
  </button>
</div>
{/if}
